<template>
    <TransitionRoot as="template" :show="open && opened">
        <Dialog
            as="div"
            static
            class="fixed z-10 inset-0 overflow-y-auto"
            @close="
                open = false;
                opened = false;
            "
            :open="open"
        >
            <div
                class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
            >
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100"
                    leave-to="opacity-0"
                >
                    <DialogOverlay
                        class="fixed inset-0 bg-opacity-75 transition-all duration-500 ease-in-out"
                        :class="{
                            'bg-gray-500': !warning,
                            'bg-red-300': warning,
                        }"
                        @click="
                            open = false;
                            opened = false;
                        "
                    />
                </TransitionChild>

                <!-- This element is to trick the browser into centering the modal contents. -->
                <span
                    class="hidden sm:inline-block sm:align-middle sm:h-screen"
                    aria-hidden="true"
                    >&#8203;</span
                >
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100 translate-y-0 sm:scale-100"
                    leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div
                        class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6"
                    >
                        <div
                            class="mx-auto flex items-left justify-left h-10 w-10 rounded-full bg-indigo-100 fixed -mt-3 ml-auto inset-x-0 top-0 hidden"
                        >
                            <PencilAltIcon class="h-5 w-5 text-indigo-600" />
                        </div>
                        <div class="mt-2">
                            <div class="text-center sm:mt-0">
                                <DialogTitle
                                    as="h3"
                                    class="text-base leading-2 font-medium text-gray-700"
                                >
                                    {{
                                        t(
                                            "poll-concepts.results-header",
                                            "Concepten voor stemmingen",
                                        )
                                    }}
                                </DialogTitle>
                                <DialogTitle
                                    as="h3"
                                    class="text-lg leading-6 font-medium text-gray-900"
                                >
                                    {{ meeting.title }}
                                </DialogTitle>

                                <DialogTitle
                                    as="h5"
                                    class="text-base leading-2 pt-1 font-medium text-gray-700"
                                >
                                    {{ meeting.datum }}
                                </DialogTitle>

                                <input
                                    type="text"
                                    tabIndex="{0}"
                                    :class="{ hidden: opened }"
                                />

                                <info-box
                                    v-if="polls && polls.length == 0"
                                    :message="message"
                                ></info-box>

                                <article class="mt-4 text-left">
                                    <div
                                        class="bg-gray-100 shadow rounded-lg mb-6 relative"
                                        v-for="(poll, index) in polls"
                                        v-bind:key="index"
                                    >
                                        <div class="p-2 px-4">
                                            {{ poll.question }}
                                            <TrashIcon
                                                v-if="poll.id"
                                                @click="deleteConcept(poll)"
                                                class="h-5 w-5 text-gray-500 inline float-right hover:text-red-700 cursor-pointer"
                                                aria-hidden="true"
                                            />
                                        </div>
                                        <div class="bg-gray-50 p-2 px-4">
                                            <div
                                                class="answer"
                                                v-for="(
                                                    answer, subindex
                                                ) in poll.answers"
                                                v-bind:key="subindex"
                                            >
                                                <span class="text-sm">
                                                    &bull; {{ answer }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </article>

                                <form
                                    v-on:submit.prevent="saveForm('normal')"
                                    novalidate
                                    class="mt-2"
                                >
                                    <section
                                        :class="{ 'opacity-30': form.delete }"
                                    >
                                        <label
                                            class="block text-sm font-medium text-gray-700 -mt-1 text-left"
                                            >{{
                                                t(
                                                    "form-poll-concepts.question",
                                                    "Vraag",
                                                )
                                            }}</label
                                        >
                                        <div
                                            class="mt-1 relative rounded-md shadow-sm text-left"
                                        >
                                            <input
                                                type="text"
                                                class="block w-full pr-10 focus:outline-none sm:text-sm rounded-md"
                                                :class="{
                                                    'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                                                        getError('question'),
                                                }"
                                                @keypress="errors = null"
                                                v-model="form.question"
                                            />
                                            <div
                                                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                            >
                                                <ExclamationCircleIcon
                                                    v-if="getError('question')"
                                                    class="h-5 w-5 text-red-500"
                                                    aria-hidden="true"
                                                />
                                            </div>
                                        </div>
                                        <p
                                            class="mt-2 text-xs text-red-600"
                                            v-if="getError('question')"
                                        >
                                            {{ getError("question", true) }}
                                        </p>

                                        <label
                                            class="block text-sm font-medium mt-3 text-gray-700 text-left"
                                            >{{
                                                t(
                                                    "form-poll-concepts.answers",
                                                    "Antwoorden",
                                                )
                                            }}</label
                                        >
                                        <div
                                            class="mt-1 relative rounded-md shadow-sm text-left"
                                        >
                                            <textarea
                                                class="block w-full pr-10 focus:outline-none sm:text-sm rounded-md"
                                                ref="myTextarea"
                                                v-model="form.answers"
                                                :rows="
                                                    form.answers &&
                                                    form.answers.split(
                                                        /\r\n|\r|\n/,
                                                    ).length + 1
                                                "
                                                :class="{
                                                    'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                                                        getError('answers'),
                                                }"
                                                @keypress="errors = null"
                                            />

                                            <div
                                                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                            >
                                                <ExclamationCircleIcon
                                                    v-if="getError('answers')"
                                                    class="h-5 w-5 text-red-500"
                                                    aria-hidden="true"
                                                />
                                            </div>
                                        </div>
                                        <p
                                            class="mt-2 text-left text-sm text-red-600"
                                            v-if="incorrectOptions"
                                        >
                                            {{
                                                t(
                                                    "poll-concepts.not-enoug-options",
                                                    "Minimaal 2 antwoorden verplicht",
                                                )
                                            }}
                                        </p>
                                        <p
                                            class="text-gray-500 mt-2 text-left text-sm"
                                        >
                                            {{ t("tts.polls.new-row") }}
                                        </p>
                                    </section>

                                    <div class="mt-2 sm:mt-6">
                                        <button
                                            type="submit"
                                            class="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm disabled:opacity-50"
                                            :class="{
                                                'bg-red-600 text-red-50 hover:bg-red-700':
                                                    form.delete,
                                                'bg-blue-600 text-blue-50 hover:bg-blue-700':
                                                    !form.delete,
                                            }"
                                            :disabled="loading"
                                        >
                                            <svg
                                                v-if="loading"
                                                class="animate-spin h-5 w-5 mr-4"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    class="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    stroke-width="4"
                                                ></circle>
                                                <path
                                                    class="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                ></path>
                                            </svg>
                                            {{
                                                t(
                                                    "poll-concepts.save",
                                                    "Opslaan",
                                                )
                                            }}
                                        </button>
                                    </div>

                                    <div class="mt-2 sm:mt-2 -mb-1">
                                        <a
                                            class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 text-base font-medium sm:text-sm disabled:opacity-50 bg-blue-0 text-blue-800 cursor-pointer hover:text-blue-900"
                                            fo-click="closeModal()"
                                            @click="saveForm('now')"
                                        >
                                            {{
                                                t(
                                                    "poll-concepts.save-and-close",
                                                    "Opslaan en sluiten",
                                                )
                                            }}
                                        </a>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script>
import {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import {
    CheckIcon,
    ExclamationCircleIcon,
    PencilAltIcon,
} from "@heroicons/vue/outline";
import { TrashIcon } from "@heroicons/vue/solid";
import Swal from "sweetalert2";
import { ref } from "vue";
import InfoBox from "../../../components/InfoBox.vue";

export default {
    components: {
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        CheckIcon,
        InfoBox,
        PencilAltIcon,
        ExclamationCircleIcon,
        TrashIcon,
    },
    mounted() {
        this.getPolls();
        Eventing.listen("closeModal", () => (this.open = false));
    },
    created() {
        this.message = this.t(
            "poll-concepts.no-polls",
            "Geen concept stemmingen bekend",
        );
    },
    data() {
        const open = ref(false);

        return {
            open,
            opened: false,
            meeting: null,
            warning: false,
            polls: null,
            incorrectOptions: false,
            message: "Geen concepten bekend",
            form: {
                question: "",
                answers: "",
            },
            loading: false,
            errors: null,
        };
    },
    methods: {
        getPolls() {
            var self = this;
            axios
                .get(
                    "/api/poll-concepts/" +
                        self.$route.params.id +
                        "?polls=true&results=true",
                )
                .then((response) => {
                    console.info(response.data.data);
                    this.polls = response.data.data;
                    this.meeting = response.data.meeting;
                    this.open = true;
                    Eventing.fire("formMeeting");
                    setTimeout(() => {
                        self.opened = true;
                    }, 400);
                })
                .catch((error) => {
                    // alert(error.response.status);
                });
        },
        getError(error, text) {
            try {
                if (this.errors) {
                    let errors = Object.values(this.errors);
                    if (text) {
                        return this.errors[error].toString();
                    }
                    return this.errors[error].toString() ? true : false;
                }
            } catch (error) {
                return false;
            }
            return false;
        },
        closeModal() {
            this.open = false;
        },
        saveForm(saveAndClose) {
            let self = this;

            this.incorrectOptions = false;

            // try {
            //     if (this.form.question.split("\n").length < 2) {
            //         this.incorrectOptions = true;
            //         this.loading = false;
            //         return;
            //     }
            // } catch (error) {
            //     alert(error);
            // }

            try {
                if (
                    this.form.answers.length <= 1 &&
                    this.form.question.length <= 1
                ) {
                    if (saveAndClose == "now") {
                        self.closeModal();
                        return;
                    }
                }
            } catch (error) {
                if (saveAndClose == "now") {
                    self.closeModal();

                    return;
                }
            }
            this.loading = true;
            // alert(self.$route.params.id);
            axios({
                method: "post",
                url: "/api/poll-concepts",
                data: {
                    room: self.$route.params.id,
                    answers: this.form.answers,
                    question: this.form.question,
                },
            })
                .then((response) => {
                    this.loading = false;
                    Eventing.fire("notify", {
                        title: self.t(
                            "poll-concepts.success-title",
                            "Concept toegevoegd",
                        ),
                        message: self.t("poll-concepts.success-text", "Gelukt"),
                        type: "success",
                        // position: "center",
                    });
                    self.form.answers = "";
                    self.form.question = "";
                    Eventing.fire("formMeeting");
                    self.open = false;

                    if (saveAndClose == "now") {
                        self.closeModal();
                    } else {
                        self.getPolls();
                        self.closeModal();
                    }
                    setTimeout(() => {
                        location.reload();
                    }, 1000);
                })
                .catch((error) => {
                    if (error.response.status == 404) {
                        this.$router.push("/login");
                    } else if (error.response.status == 422) {
                        this.errors = error.response.data.errors;
                        // this.texts.content = errors["pincode"].toString();
                    } else if (error.response.status == 409) {
                        this.incorrectOptions = true;
                        this.loading = false;
                        // return;
                        // this.texts.content = error.response.data.error;
                    } else {
                        this.closeModal();
                        // alert("Daar ging wat mis");
                    }
                    this.loading = false;
                });
        },
        deleteConcept(poll) {
            let self = this;
            self.chatOption = false;
            Swal.fire({
                title: self.t(
                    "poll-concepts.delete-concept-poll",
                    "Concept verwijderen?",
                ),
                text: self.t(
                    "poll-concepts.delete-concept-poll-text",
                    "Zeker weten?",
                ),
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#de3618",
                cancelButtonColor: "#333",
                confirmButtonText: self.t("tts.yes"),
                cancelButtonText: self.t("tts.cancel"),
            }).then((result) => {
                if (result.value) {
                    axios
                        .delete("/api/poll-concepts/" + poll.id)
                        .then((response) => {
                            self.opened = false;
                            // self.getPolls();
                            var room = self.$route.params.id;
                            Eventing.fire("notify", {
                                title: self.t(
                                    "poll-concepts.deleted-title",
                                    "Concept gewist",
                                ),
                                message: self.t(
                                    "poll-concepts.deleted-text",
                                    "Gewist",
                                ),
                                type: "success",
                                // position: "center",
                            });
                            this.$router.push(
                                "/settings/meetings/poll-concepts/" + room,
                            );
                            Eventing.fire("formMeeting");
                            window.location =
                                "/app#/settings/meetings/poll-concepts/" + room;
                        })
                        .catch((error) => {});
                }
            });
        },
        checkDelete() {
            var val = this.form.delete;
            if (val == true) {
                Eventing.fire("warning");
            } else {
                Eventing.fire("noWarning");
            }
        },
    },
};
</script>
